import React, { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Calendar as CalendarIcon,
  Utensils,
  Plus,
  Info,
  BarChart3,
} from "lucide-react";
import { menuService } from "@/services";
import MenuSelectionModal from "@/components/business/products/MenuSelectionModal";
import { useToast } from "@/hooks/useToast";

/**
 * Página de gestión de menús diarios
 *
 * Permite al administrador configurar qué productos se ofrecen
 * como "Menú del Día" en fechas específicas
 * Optimizada para dispositivos móviles con layout responsive.
 */
const MenuManagementPage: React.FC = () => {
  const [date, _] = useState<Date | undefined>(new Date());
  const [dailyMenus, setDailyMenus] = useState<Record<string, string[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { toast } = useToast();

  // Cargar menús al montar el componente
  useEffect(() => {
    loadDailyMenus();
  }, []);

  const loadDailyMenus = async () => {
    setIsLoading(true);
    try {
      const menus = await menuService.getDailyMenus();
      setDailyMenus(menus);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los menús diarios",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      setSelectedDate(selectedDate);
      setIsModalOpen(true);
    }
  };

  const handleMenuUpdated = () => {
    loadDailyMenus();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-CL", {
      day: "numeric",
      month: "short",
    });
  };

  const getMenuCount = (dateString: string) => {
    return dailyMenus[dateString]?.length || 0;
  };

  // const renderCalendarDay = (day: Date) => {
  //   const dateString = day.toISOString().split("T")[0];
  //   const menuCount = getMenuCount(dateString);
  //   const hasMenu = menuCount > 0;

  //   return (
  //     <div className="relative w-full h-full flex items-center justify-center">
  //       <span className="text-sm">{day.getDate()}</span>
  //       {hasMenu && (
  //         <div className="absolute -top-1 -right-1">
  //           <Badge
  //             variant="default"
  //             className="h-5 w-5 p-0 flex items-center justify-center text-xs bg-primary"
  //           >
  //             {menuCount}
  //           </Badge>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  const getUpcomingMenus = () => {
    const today = new Date();
    const upcomingDates = Object.keys(dailyMenus)
      .filter((dateString) => new Date(dateString) >= today)
      .sort()
      .slice(0, 5);

    return upcomingDates;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Gestión de Menús Diarios
          </h1>
          <p className="text-muted-foreground mt-2">
            Configura qué productos se ofrecen como "Menú del Día" en fechas
            específicas
          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 h-10"
        >
          <Plus className="h-4 w-4" />
          Nuevo Menú
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendario Principal */}
        <div className="lg:col-span-2">
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <CalendarIcon className="h-5 w-5" />
                Calendario de Menús
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Haz clic en cualquier día para configurar el menú del día
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">
                    Cargando calendario...
                  </span>
                </div>
              ) : (
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={handleDateSelect}
                    className="rounded-md border border-border"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Panel Lateral */}
        <div className="space-y-6">
          {/* Estadísticas */}
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <BarChart3 className="h-5 w-5" />
                Estadísticas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Total de menús configurados
                </span>
                <Badge variant="secondary" className="text-xs">
                  {Object.keys(dailyMenus).length}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Productos en menús
                </span>
                <Badge variant="secondary" className="text-xs">
                  {Object.values(dailyMenus).reduce(
                    (total, products) => total + products.length,
                    0
                  )}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Próximos Menús */}
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                <Utensils className="h-4 w-4" />
                Próximos Menús
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getUpcomingMenus().length > 0 ? (
                <div className="space-y-3">
                  {getUpcomingMenus().map((dateString) => (
                    <div
                      key={dateString}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors border border-border"
                      onClick={() => {
                        setSelectedDate(new Date(dateString));
                        setIsModalOpen(true);
                      }}
                    >
                      <div>
                        <p className="font-medium text-sm text-foreground">
                          {formatDate(dateString)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {getMenuCount(dateString)} productos
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        Ver
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Utensils className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay menús configurados</p>
                  <p className="text-xs">
                    Haz clic en el calendario para crear uno
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Información */}
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                <Info className="h-5 w-5" />
                Información
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    Los días con menú configurado se muestran con un indicador
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p>Haz clic en cualquier día para configurar el menú</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p>Puedes seleccionar múltiples productos por día</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal de Selección */}
      <MenuSelectionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDate(null);
        }}
        selectedDate={selectedDate}
        onMenuUpdated={handleMenuUpdated}
      />
    </div>
  );
};

export default MenuManagementPage;
